import * as Sentry from '@sentry/react'
import React from 'react'
import { playerLogger } from '@/utils/player-logger'
import { PLAYER_COLORS } from '@/utils/player-theme'
import { FallbackScreen } from './fallback-screen'

interface State {
  hasError: boolean
  error: Error | null
}

/**
 * Captura qualquer exceção não tratada nos renderers ou screens.
 * Em produção exibe FallbackScreen e força reload após 10s para auto-recovery.
 * Em desenvolvimento mostra a mensagem de erro para facilitar debug.
 */
export class PlayerErrorBoundary extends React.Component<{ children: React.ReactNode }, State> {
  state: State = { hasError: false, error: null }

  private reloadTimer: ReturnType<typeof setTimeout> | null = null

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    playerLogger.error('[PlayerErrorBoundary] Erro crítico capturado:', error)
    Sentry.captureException(error, { extra: { componentStack: info.componentStack } })

    // Auto-recovery: reload após 10s para retomar a reprodução
    this.reloadTimer = setTimeout(() => {
      window.location.reload()
    }, 10_000)
  }

  componentWillUnmount() {
    if (this.reloadTimer) clearTimeout(this.reloadTimer)
  }

  render() {
    if (!this.state.hasError) return this.props.children

    if (import.meta.env.DEV && this.state.error) {
      return (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: PLAYER_COLORS.bgDark,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 16,
            padding: 40
          }}
        >
          <div style={{ color: PLAYER_COLORS.textError, fontFamily: 'monospace', fontSize: 14, fontWeight: 700 }}>
            [DEV] PlayerErrorBoundary
          </div>
          <pre
            style={{
              color: '#f87171',
              fontFamily: 'monospace',
              fontSize: 11,
              background: PLAYER_COLORS.errorBg,
              border: `1px solid ${PLAYER_COLORS.errorBorder}`,
              borderRadius: 8,
              padding: 24,
              maxWidth: 720,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word'
            }}
          >
            {this.state.error.message}
            {'\n\n'}
            {this.state.error.stack?.split('\n').slice(0, 6).join('\n')}
          </pre>
        </div>
      )
    }

    // Produção: exibe FallbackScreen (relógio + logo) enquanto aguarda reload
    return <FallbackScreen />
  }
}
